/**
 * Workout Segment Parser
 *
 * Parses planned workout text into an array of loggable segments
 * for the training log modal.
 *
 * Segment types:
 *   'interval'       — NxD[unit] with per-rep splits (e.g. "4x200")
 *   'timed_interval' — Nx M' with per-rep timing (e.g. "6x3'")
 *   'continuous'     — Distance-based run (warmup, cooldown, easy run)
 *   'gc'             — Gear Change: outer distance + sub-segments (e.g. "1600m GC (400/800/400)")
 *   'xt'             — Cross-training (bike, swim, etc.)
 */

const METERS_PER_MILE = 1609.344;

// Tag key → VDOT zone key (mirrors paceTags.js)
const TAG_TO_ZONE = {
  RECOVERY: 'recovery', EASY: 'easy', MODERATE: 'moderate',
  SUBTEMPO: 'sub_tempo', TEMPO: 'tempo', LT2: 'lt2', LT1: 'lt1',
  APOWER: 'aerobic_power', VO2MAX: 'vo2max', MIXED: 'mixed_zone',
  ANCAP: 'anaerobic_capacity', ANPOWER: 'anaerobic_power',
  SE: 'speed_endurance', SPEED: 'speed',
};

const XT_KEYWORDS = /\b(bik(?:e|ing)|cycl(?:e|ing)|swim(?:ming)?|pool|elliptical|xt\b|cross[\s-]?train(?:ing)?|arc[\s-]?trainer|row(?:ing)?|aqua[\s-]?jog(?:ging)?)\b/i;

// Activities shown as simple checkboxes in the log (completed or not)
const CHECKBOX_ACTIVITIES_RE = /^\s*(?:active\s+(?:warm.?up|wu)|drills?|core(?:\s*\(?\s*(?:navy|gold)\s*\)?)?|fam\b|strides?\b|mobility\s+[ab]\b|strength\s+[ab]\b)\s*$/i;

// Non-running drills / mobility — these should not become loggable segments
const NON_RUNNING_RE = /^\s*(?:active\s+(?:warm.?up|wu)|dynamic(?:\s+(?:warm.?up|stretch(?:ing)?|drill(?:s)?))?|static\s+stretch(?:ing)?|(?:form\s+)?drill(?:s)?|[ab]-?skip(?:s)?|high.?knees?|butt.?kicks?|core(?:\s+work)?|strength(?:\s+(?:work|circuit|routine))?|plank(?:s)?|push.?ups?|sit.?ups?|foam.?roll(?:ing)?|mobil(?:ity)?(?:\s+work)?|activation(?:\s+drill(?:s)?)?|hip\s+(?:circles?|swings?|flexor(?:s)?)|leg\s+swings?|ankle\s+(?:circles?|mobility)|lunge(?:s)?|hurdle\s+mobil(?:ity)?|dynamic)\s*$/i;

// Running keywords — if present in otherwise unrecognized text, still create a segment
const RUNNING_HINT_RE = /\b(?:wu|warm.?up|cd|cool.?down|jog(?:ging)?|easy|run(?:ning)?|miles?|mi\b|km\b|tempo|fartlek|stride(?:s)?|build(?:up)?|aerobic|threshold|long\s+run|recovery\s+run)\b/i;

function normalizeUnit(u) {
  if (!u) return 'm';
  const l = u.toLowerCase();
  if (l.startsWith('mi')) return 'mi';
  if (l === 'km' || l === 'k') return 'km';
  return 'm';
}

/** Extract planned rest time from a segment string, returns seconds */
function parseRestSeconds(text) {
  let m;
  // "w/ 3min rest", "with 2 minutes"
  m = text.match(/(?:w\/?|with)\s+(\d+(?:\.\d+)?)\s*min(?:utes?)?\s*(?:rest|off|recovery|jog)?/i);
  if (m) return Math.round(parseFloat(m[1]) * 60);
  // "w/ 75" r", "w/ 90" rest"  (double-quote = seconds)
  m = text.match(/(?:w\/?|with)\s+(\d+(?:\.\d+)?)["\u201d]\s*(?:r(?:est)?|off|recovery)?/i);
  if (m) { const v = parseFloat(m[1]); if (v <= 600) return Math.round(v); }
  // standalone "3 min rest", "2 min"
  m = text.match(/(\d+(?:\.\d+)?)\s*min(?:utes?)?\s*(?:rest|off|recovery)?/i);
  if (m) return Math.round(parseFloat(m[1]) * 60);
  // "90s rest", "60 sec"
  m = text.match(/(\d+(?:\.\d+)?)\s*s(?:ec(?:onds?)?)?\s*(?:rest|off|recovery)?/i);
  if (m) {
    const v = parseFloat(m[1]);
    if (v <= 600) return Math.round(v); // sanity: must be < 10 min
  }
  // "75"" / "75" r" / "75" rest"  (double-quote = seconds, standalone)
  m = text.match(/\b(\d+(?:\.\d+)?)["\u201d]\s*(?:r(?:est)?|off|recovery)?(?:\s|$|[,;])/i);
  if (m) { const v = parseFloat(m[1]); if (v <= 600) return Math.round(v); }
  // "on 1'" or "on 1' recovery"
  m = text.match(/\bon\s+(\d+(?:\.\d+)?)['\u2019]/i);
  if (m) return Math.round(parseFloat(m[1]) * 60);
  // "on 30"" or "on 30" recovery" (seconds)
  m = text.match(/\bon\s+(\d+(?:\.\d+)?)["\u201d]/i);
  if (m) { const v = parseFloat(m[1]); if (v <= 600) return Math.round(v); }
  // "1' off", "1' recovery", "1' rest" (no leading "on")
  m = text.match(/\b(\d+(?:\.\d+)?)['\u2019]\s+(?:off|recovery|rest|jog)\b/i);
  if (m) return Math.round(parseFloat(m[1]) * 60);
  // "30" off", "30" recovery" (seconds, no leading "on")
  m = text.match(/\b(\d+(?:\.\d+)?)["\u201d]\s+(?:off|recovery|rest|jog)\b/i);
  if (m) { const v = parseFloat(m[1]); if (v <= 600) return Math.round(v); }
  // "1:30 rest"
  m = text.match(/(\d):([0-5]\d)\s*(?:rest|off|recovery)/i);
  if (m) return parseInt(m[1]) * 60 + parseInt(m[2]);
  return 90; // default 90 seconds
}

/** Parse a cycle time (total work+rest period) from text. Returns seconds, or 0. */
function parseCycleSeconds(text) {
  let m;
  // "4' cycle", "4 min cycle"
  m = text.match(/(\d+(?:\.\d+)?)\s*(?:min(?:utes?)?|['\u2019])\s*cycle/i);
  if (m) return Math.round(parseFloat(m[1]) * 60);
  // "60" cycle", "60s cycle", "60 sec cycle"
  m = text.match(/(\d+(?:\.\d+)?)\s*(?:s(?:ec(?:onds?)?)?|["\u201d])\s*cycle/i);
  if (m) { const v = parseFloat(m[1]); if (v <= 600) return Math.round(v); }
  return 0;
}

/** Produce a human-readable label for a segment */
function getSegmentLabel(text) {
  const t = text.replace(/\[[^\]]+\]/g, '').trim();
  // Set/group: "3x(500m, 300m, 200m)" or "(1', 2', 3', 2', 1')"
  const setLabelM = t.match(/^(?:(\d+)\s*[xX\u00d7]\s*)?\(([^)]+)\)/);
  if (setLabelM) {
    const inner = setLabelM[2].replace(/\s+/g, '').replace(/,/g, ', ');
    return setLabelM[1] ? `${setLabelM[1]}\u00d7(${inner})` : `(${inner})`;
  }
  if (/\bactive\s+(?:WU\b|warm.?up\b)/i.test(t)) return 'Active Warm-up';
  if (/\bWU\b|\bwarm.?up\b/i.test(t)) return 'Warm-up';
  if (/\bCD\b|\bcool.?down\b/i.test(t)) return 'Cool-down';
  if (/\bjog\b/i.test(t) && !/[xX\u00d7]/.test(t)) return 'Easy Jog';
  if (/^\s*fam\s*$/i.test(t)) return 'FAM';
  if (/\bstride/i.test(t)) return 'Strides';
  if (/\bflys?\b/i.test(t)) {
    // Try to extract distance immediately before "fly/flys" e.g. "(30 flys)" or "30m flys"
    const dm = t.match(/(\d+(?:\.\d+)?)\s*(m(?:eters?)?|km|k|miles?|mi)?\s*flys?\b/i);
    if (dm) return `${dm[1]}${normalizeUnit(dm[2])} Flys`;
    return 'Flys';
  }
  // GC (Gear Change): "1600m GC (400/800/400)"
  if (/\bGC\b/i.test(t)) {
    const gcFull = t.match(/(\d+(?:\.\d+)?)\s*(m(?:eters?)?|km|k|miles?|mi)?\s*GC\s*\(([^)]+)\)/i);
    if (gcFull) return `${gcFull[1]}${normalizeUnit(gcFull[2])} GC (${gcFull[3]})`;
    const dm = t.match(/(\d+(?:\.\d+)?)\s*(m(?:eters?)?|km|k|miles?|mi)?/i);
    if (dm) return `${dm[1]}${normalizeUnit(dm[2])} GC`;
    return 'GC';
  }
  // Timed interval: NxM' (minutes) or Nx30" (seconds)
  const tm = t.match(/(\d+)\s*[xX\u00d7]\s*(\d+(?:\.\d+)?)(['\u2019""\u201d])/i);
  if (tm) {
    const isSeconds = tm[3] === '"' || tm[3] === '\u201d';
    return `${tm[1]}\u00d7${tm[2]}${isSeconds ? '"' : "'"}`;
  }
  // Distance interval: NxD[unit]
  const im = t.match(/(\d+)\s*[xX\u00d7]\s*(\d+(?:\.\d+)?)\s*(miles?|mi|km|k|m)?/i);
  if (im) {
    const unit = im[3] ? normalizeUnit(im[3]) : 'm';
    return `${im[1]}\u00d7${im[2]}${unit}`;
  }
  // XT
  const xtM = t.match(/\b(bik(?:e|ing)|cycl(?:e|ing)|swim(?:ming)?|elliptical|row(?:ing)?)\b/i);
  if (xtM) {
    const w = xtM[0];
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }
  // Distance-only label
  const dm = t.match(/(\d+(?:\.\d+)?)\s*(miles?|mi|km|k)/i);
  if (dm) return `${dm[1]} ${normalizeUnit(dm[2])}`;
  return t.slice(0, 35) || 'Segment';
}

/** Parse one text fragment into a segment object */
function parseSegmentFromText(raw) {
  // Strip session prefix (AM: / PM:) if present, then pace tags
  const withoutPrefix = raw.replace(/^(?:am|pm)\s*:\s*/i, '');
  const t = withoutPrefix.replace(/\[[^\]]+\]/g, ' ').trim();
  if (!t) return null;

  // Checkbox activities — simple completion tracking
  if (CHECKBOX_ACTIVITIES_RE.test(t)) {
    return { type: 'checkbox', checked: false };
  }

  // N strides or N x D[unit] strides — checkbox only, no times needed
  if (/^\(?\d+(?:\s*[xX\u00d7]\s*\d+(?:\.\d+)?\s*(?:miles?|mi|km|k|m)?)?\s+strides?\b/i.test(t)) {
    return { type: 'checkbox', checked: false };
  }

  // Any segment mentioning flys — always a drill checkbox, never needs splits
  if (/\bflys?\b/i.test(t)) {
    return { type: 'checkbox', checked: false };
  }

  // Non-running drills / mobility — skip entirely
  if (NON_RUNNING_RE.test(t)) return null;

  // Set group: Nx(entries) or (entries) — distances or timed segments
  // Distance: "3x(500m, 300m, 200m) w/ 60" r"
  // Timed: "(1'[LT2], 1'[RECOVERY], 2'[LT2], 1'[RECOVERY], 3'[LT2])"
  const setM = t.match(/^(?:(\d+)\s*[xX\u00d7]\s*)?\(([^)]+)\)/);
  if (setM) {
    const sets = setM[1] ? Math.max(1, parseInt(setM[1], 10)) : 1;
    const rawInner = withoutPrefix.match(/\(([^)]+)\)/)?.[1] ?? '';
    const innerParts = rawInner.split(',').map(p => p.trim()).filter(Boolean);
    const tagMatches = innerParts.map(p => {
      const tm = p.match(/\[([A-Z][A-Z0-9]*)\]/i);
      return tm ? TAG_TO_ZONE[tm[1].toUpperCase()] ?? null : null;
    });

    // Check if entries are timed (contain ' or ") or distance-based
    const firstClean = innerParts[0]?.replace(/\[[^\]]+\]/g, '').trim() ?? '';
    const isTimed = /^\d+(?:\.\d+)?\s*['\u2019""\u201d]/.test(firstClean);

    if (isTimed) {
      // Timed set group — each entry is a duration
      const durations = [];
      for (const part of innerParts) {
        const clean = part.replace(/\[[^\]]+\]/g, '').trim();
        const tm = clean.match(/^(\d+(?:\.\d+)?)\s*(['\u2019""\u201d])/);
        if (tm) {
          const isSeconds = tm[2] === '"' || tm[2] === '\u201d';
          durations.push(isSeconds ? Math.round(parseFloat(tm[1])) : Math.round(parseFloat(tm[1]) * 60));
        }
      }
      if (durations.length > 0) {
        const totalReps = sets * durations.length;
        const repDurations = [];
        const repPaceZones = [];
        for (let s = 0; s < sets; s++) {
          for (let d = 0; d < durations.length; d++) {
            repDurations.push(durations[d]);
            repPaceZones.push(tagMatches[d]);
          }
        }
        return {
          type: 'timed_interval',
          reps: totalReps,
          duration: durations[0],
          repDurations,
          repPaceZones,
          rest: 0,
          restType: 'timed',
          splits: Array(totalReps).fill(''),
          rests: Array(totalReps).fill(''),
        };
      }
    } else {
      // Distance set group — existing logic
      const distances = [];
      const units = [];
      for (const part of innerParts) {
        const clean = part.replace(/\[[^\]]+\]/g, '').trim();
        const dm = clean.match(/^(\d+(?:\.\d+)?)\s*(miles?|mi|km|k|m(?:eters?)?)?/i);
        if (dm) {
          distances.push(parseFloat(dm[1]));
          units.push(normalizeUnit(dm[2]));
        }
      }
      if (distances.length > 0) {
        const totalReps = sets * distances.length;
        const repDistances = [];
        const repDistanceUnits = [];
        const repPaceZones = [];
        for (let s = 0; s < sets; s++) {
          for (let d = 0; d < distances.length; d++) {
            repDistances.push(distances[d]);
            repDistanceUnits.push(units[d]);
            repPaceZones.push(tagMatches[d]);
          }
        }
        const rest = parseRestSeconds(t);
        return {
          type: 'interval',
          reps: totalReps,
          distance: distances[0],
          distUnit: units[0],
          rest,
          restType: 'timed',
          cycleSeconds: 0,
          repDistances,
          repDistanceUnits,
          repPaceZones,
          splits: Array(totalReps).fill(''),
          rests: Array(totalReps).fill(''),
        };
      }
    }
  }

  // Timed interval: Nx M' (minutes) or Nx 30" (seconds) — must check before distance interval and XT
  const timedM = t.match(/^(\d+(?:\.\d+)?)\s*[xX\u00d7]\s*(\d+(?:\.\d+)?)(['\u2019""\u201d])/i);
  if (timedM) {
    const reps = Math.max(1, Math.round(parseFloat(timedM[1])));
    const isSeconds = timedM[3] === '"' || timedM[3] === '\u201d';
    const durationSec = isSeconds
      ? Math.round(parseFloat(timedM[2]))
      : Math.round(parseFloat(timedM[2]) * 60);
    const isJogDown = /jog\s+down/i.test(t);
    const rest = isJogDown ? 0 : parseRestSeconds(t);
    return {
      type: 'timed_interval',
      reps,
      duration: durationSec,
      rest,
      restType: isJogDown ? 'jog_down' : 'timed',
      splits: Array(reps).fill(''),
      rests: Array(reps).fill(''),
    };
  }

  // Distance interval: NxD[unit] (check before XT — "cycle" in "60" cycle" must not trigger XT)
  // Negative lookahead excludes ' (minutes), " (seconds), and word chars to avoid false matches
  const intervalM = t.match(/^(\d+(?:\.\d+)?)\s*[xX\u00d7]\s*(\d+(?:\.\d+)?)\s*(miles?|mi|km|k|m)?(?!['\u2019""\u201d\w])/i);
  if (intervalM) {
    const reps = Math.max(1, Math.round(parseFloat(intervalM[1])));
    const dist = parseFloat(intervalM[2]);
    const unit = normalizeUnit(intervalM[3]);
    const isJogDown = /jog\s+down/i.test(t);
    const isEqualRest = /equal\s+rest/i.test(t);
    const cycleSeconds = parseCycleSeconds(t);
    const rest = isJogDown || isEqualRest || cycleSeconds > 0 ? 0 : parseRestSeconds(t);
    const restType = isJogDown ? 'jog_down' : isEqualRest ? 'equal' : cycleSeconds > 0 ? 'cycle' : 'timed';
    return {
      type: 'interval',
      reps,
      distance: dist,
      distUnit: unit,
      rest,
      restType,
      cycleSeconds,
      splits: Array(reps).fill(''),
      rests: Array(reps).fill(''),
    };
  }

  // GC (Gear Change): "1600m GC (400/800/400) [SPEED] [LT2] [SPEED]"
  const gcM = t.match(/(\d+(?:\.\d+)?)\s*(m(?:eters?)?|km|k|miles?|mi)?\s*GC\s*\(([^)]+)\)/i);
  if (gcM) {
    const outerDist = parseFloat(gcM[1]);
    const outerUnit = normalizeUnit(gcM[2]);
    const partStrs = gcM[3].split('/').map(p => p.trim());
    // Tags are in withoutPrefix (t has them stripped)
    const tagKeys = [...withoutPrefix.matchAll(/\[([A-Z][A-Z0-9]*)\]/gi)].map(m => m[1].toUpperCase());
    const gcParts = partStrs.map((p, i) => {
      const dm = p.match(/^(\d+(?:\.\d+)?)\s*(m(?:eters?)?|km|k|miles?|mi)?$/i);
      return {
        dist: dm ? parseFloat(dm[1]) : null,
        distUnit: dm ? normalizeUnit(dm[2]) : 'm',
        paceZone: tagKeys[i] ? (TAG_TO_ZONE[tagKeys[i]] ?? null) : null,
      };
    });
    return {
      type: 'gc',
      distance: outerDist,
      distUnit: outerUnit,
      gcParts,
      splits: Array(partStrs.length).fill(''),
      time: '',
    };
  }
  // GC without parens — fallback to continuous
  const gcSimple = t.match(/(\d+(?:\.\d+)?)\s*(m(?:eters?)?|km|k|miles?|mi)?\s*GC\b/i);
  if (gcSimple) {
    return { type: 'continuous', distance: parseFloat(gcSimple[1]), distUnit: normalizeUnit(gcSimple[2]), time: '' };
  }

  // Cross-training
  if (XT_KEYWORDS.test(t)) {
    let plannedDuration = '';
    // Range: "40'-45'" or "40-45 min" (check before single-value patterns)
    const dmRangeQuote = t.match(/(?<![xX\u00d7\d])(\d+(?:\.\d+)?)\s*['\u2019]\s*[-\u2013]\s*(\d+(?:\.\d+)?)\s*['\u2019]/);
    const dmRangeWord = !dmRangeQuote && t.match(/(\d+(?:\.\d+)?)\s*[-\u2013]\s*(\d+(?:\.\d+)?)\s*min(?:utes?)?/i);
    // Single value fallbacks
    const dmMin = !dmRangeQuote && !dmRangeWord && t.match(/(?<![xX\u00d7\d])(\d+(?:\.\d+)?)\s*['\u2019]/);
    const dmWord = !dmRangeQuote && !dmRangeWord && !dmMin && t.match(/(\d+(?:\.\d+)?)\s*min(?:utes?)?/i);
    const dmColon = !dmRangeQuote && !dmRangeWord && !dmMin && !dmWord && t.match(/(\d{1,2}):([0-5]\d)/);
    if (dmRangeQuote) plannedDuration = `${dmRangeQuote[1]}'-${dmRangeQuote[2]}'`;
    else if (dmRangeWord) plannedDuration = `${dmRangeWord[1]}-${dmRangeWord[2]} min`;
    else if (dmMin) plannedDuration = `${dmMin[1]}'`;
    else if (dmWord) plannedDuration = `${dmWord[1]} min`;
    else if (dmColon) plannedDuration = `${dmColon[1]}:${dmColon[2]}`;
    return { type: 'xt', duration: plannedDuration };
  }

  // Single timed segment: "10'[RECOVERY]", "10' @ [RECOVERY]", "10' at [EASY]"
  // Must match against withoutPrefix (not t) since tags are needed
  const singleTimedM = withoutPrefix.match(/^(\d+(?:\.\d+)?)\s*['\u2019]\s*(?:@\s*|at\s+)?\[([A-Z][A-Z0-9]*)\]/i);
  if (singleTimedM) {
    const minutes = parseFloat(singleTimedM[1]);
    const m = Math.floor(minutes);
    const s = Math.round((minutes - m) * 60);
    const timeStr = `${m}:${String(s).padStart(2, '0')}`;
    return {
      type: 'continuous',
      distance: '',
      distUnit: 'mi',
      time: timeStr,
      paceZone: TAG_TO_ZONE[singleTimedM[2].toUpperCase()] || null,
    };
  }

  // Standalone miles
  const miM = t.match(/(\d+(?:\.\d+)?)\s*(?:miles?|mi)(?!\w)/i);
  if (miM) {
    return { type: 'continuous', distance: parseFloat(miM[1]), distUnit: 'mi', time: '' };
  }
  // Standalone km
  const kmM = t.match(/(\d+(?:\.\d+)?)\s*(?:km|k)(?!\w)/i);
  if (kmM) {
    return { type: 'continuous', distance: parseFloat(kmM[1]), distUnit: 'km', time: '' };
  }
  // Standalone meters (50-10000) — allows trailing text like "w/ 60" r"
  const mM = t.match(/^(\d+)\s*m(?:eters?)?(?!\w)/i) || t.match(/^(\d+)$/);
  if (mM) {
    const n = parseInt(mM[1], 10);
    if (n >= 50 && n <= 10000) {
      return { type: 'continuous', distance: n, distUnit: 'm', time: '' };
    }
  }

  // Fallback: only create a continuous segment if the text looks like running
  if (RUNNING_HINT_RE.test(t)) {
    return { type: 'continuous', distance: '', distUnit: 'mi', time: '' };
  }
  return null;
}

/** Extract paceZone (and restZone for on/off timed intervals) from raw text tags */
function extractPaceZones(raw, seg) {
  // GC segments store zones per-part in gcParts, not on the segment itself
  if (seg.type === 'gc') return;
  const tagMatches = [...raw.matchAll(/\[([A-Z][A-Z0-9]*)\]/gi)];
  if (!tagMatches.length) return;
  const firstZone = TAG_TO_ZONE[tagMatches[0][1].toUpperCase()];
  if (firstZone) seg.paceZone = firstZone;
  if (seg.type === 'timed_interval' && tagMatches.length >= 2) {
    const restZone = TAG_TO_ZONE[tagMatches[1][1].toUpperCase()];
    if (restZone) seg.restZone = restZone;
  }
  // Progression: [TAG] > [TAG] — store end zone for interpolated targets
  if ((seg.type === 'continuous' || seg.type === 'interval') && tagMatches.length >= 2 && />\s*\[/i.test(raw)) {
    const endZone = TAG_TO_ZONE[tagMatches[tagMatches.length - 1][1].toUpperCase()];
    if (endZone) seg.progressionEndZone = endZone;
  }
}

/** Split workout text on commas/newlines/semicolons, respecting brackets */
function splitWorkoutText(text) {
  const parts = [];
  let depth = 0;
  let current = '';
  for (const ch of text) {
    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth--;
    else if ((ch === ',' || ch === '\n' || ch === ';') && depth === 0) {
      const trimmed = current.trim();
      if (trimmed) parts.push(trimmed);
      current = '';
      continue;
    }
    current += ch;
  }
  const trimmed = current.trim();
  if (trimmed) parts.push(trimmed);
  return parts;
}

const SESSION_LINE_RE = /^(am|pm)\s*:/i;

function parsePartsWithSession(parts, session) {
  return parts
    .map(part => {
      const seg = parseSegmentFromText(part);
      if (!seg) return null;
      seg.label = getSegmentLabel(part.replace(/^(?:am|pm)\s*:\s*/i, ''));
      if (session) seg.session = session;
      extractPaceZones(part, seg);
      return seg;
    })
    .filter(Boolean);
}

/**
 * Parse planned workout text into an array of loggable segment objects.
 * Supports AM:/PM: session markers on separate lines.
 * @param {string} text - raw planned workout text (may contain pace tags)
 * @returns {Segment[]}
 */
export function parseWorkoutIntoSegments(text) {
  if (!text?.trim()) {
    return [{ type: 'continuous', label: 'Workout', distance: '', distUnit: 'mi', time: '' }];
  }

  // Strip *...* description blocks — these are display-only and should not create segments.
  const withoutDescs = text.replace(/\*([^*]+)\*/g, ' ');

  // Collapse commas between GC tag lists so the splitter doesn't break them into separate segments.
  // "1600m GC (400/800/400) [SPEED], [LT2], [SPEED]" → "...[SPEED] [LT2] [SPEED]"
  const normalizedText = withoutDescs.replace(
    /(\d+(?:\.\d+)?)\s*(?:m(?:eters?)?|km|k|miles?|mi)?\s*GC\s*\([^)]+\)((?:\s*,?\s*\[[A-Z][A-Z0-9]*\])+)/gi,
    (match) => match.replace(/,\s*(?=\[)/g, ' ')
  );

  // Check for AM/PM session structure (lines starting with AM: or PM:)
  const lines = normalizedText.split('\n').map(l => l.trim()).filter(Boolean);
  const hasSessionMarkers = lines.some(l => SESSION_LINE_RE.test(l));

  if (hasSessionMarkers) {
    const allSegs = [];
    let currentSession = null;
    for (const line of lines) {
      const sessionMatch = line.match(/^(am|pm)\s*:\s*/i);
      if (sessionMatch) {
        currentSession = sessionMatch[1].toLowerCase();
        const rest = line.slice(sessionMatch[0].length).trim();
        if (rest) allSegs.push(...parsePartsWithSession(splitWorkoutText(rest), currentSession));
      } else {
        // No prefix — inherit current session (handles "Strength A" after PM block)
        allSegs.push(...parsePartsWithSession(splitWorkoutText(line), currentSession));
      }
    }
    return allSegs.length > 0
      ? allSegs
      : [{ type: 'continuous', label: 'Workout', distance: '', distUnit: 'mi', time: '' }];
  }

  // Non-session workout — original logic
  const parts = splitWorkoutText(normalizedText);
  if (parts.length === 0) {
    return [{ type: 'continuous', label: 'Workout', distance: '', distUnit: 'mi', time: '' }];
  }
  const segs = parts
    .map(part => {
      const seg = parseSegmentFromText(part);
      if (!seg) return null;
      seg.label = getSegmentLabel(part);
      extractPaceZones(part, seg);
      return seg;
    })
    .filter(Boolean);
  return segs.length > 0
    ? segs
    : [{ type: 'continuous', label: 'Workout', distance: '', distUnit: 'mi', time: '' }];
}

/**
 * Compute total running miles from completed training log segments.
 * Cross-training and timed intervals (where pace is unknown) are excluded.
 * @param {Segment[]} segments
 * @returns {number} miles
 */
export function calcTotalMilesFromLog(segments) {
  let total = 0;
  for (const seg of segments) {
    if (seg.type === 'interval') {
      const defaultDist = parseFloat(seg.distance) || 0;
      if (!defaultDist) continue;
      // Count completed reps (non-empty splits); fall back to reps count
      const completedReps = (seg.splits ?? []).filter(s => s !== '').length
        || Math.max(1, parseFloat(seg.reps) || 0);
      let mileTotal = 0;
      for (let i = 0; i < completedReps; i++) {
        const repDist = parseFloat(seg.repDistances?.[i]) || defaultDist;
        const repUnit = seg.repDistanceUnits?.[i] || seg.distUnit;
        const meters = repUnit === 'mi' ? repDist * METERS_PER_MILE
          : repUnit === 'km' ? repDist * 1000
          : repDist;
        mileTotal += meters / METERS_PER_MILE;
      }
      total += mileTotal;
    } else if (seg.type === 'timed_interval') {
      const durSec = seg.duration || 0;
      const restSec = seg.rest || 0;
      let segMiles = 0;
      function addPaceMiles(paceStr, durationSec) {
        if (!paceStr || !durationSec) return;
        const clean = paceStr.replace(/\/\w+$/, '').trim();
        const p = clean.split(':').map(Number);
        if (p.length !== 2 || p.some(isNaN)) return;
        const paceSec = p[0] * 60 + p[1];
        if (paceSec > 0) segMiles += durationSec / paceSec;
      }
      const splits = seg.splits ?? [];
      const rests = seg.rests ?? [];
      for (let i = 0; i < splits.length; i++) {
        const repDur = seg.repDurations?.[i] ?? durSec;
        addPaceMiles(splits[i], repDur);
        if (i < splits.length - 1) addPaceMiles(rests[i], restSec);
      }
      // Jog-down hills: athlete runs up then jogs back the same distance
      if (seg.restType === 'jog_down') segMiles *= 2;
      total += segMiles;
    } else if (seg.type === 'continuous' || seg.type === 'gc') {
      const dist = parseFloat(seg.distance) || 0;
      if (!dist) continue;
      if (seg.distUnit === 'mi') total += dist;
      else if (seg.distUnit === 'km') total += dist / 1.60934;
      else total += dist / METERS_PER_MILE;
    }
  }
  return total;
}

/** Parse a duration display string to seconds. Handles ranges by taking midpoint. */
export function parseDurationStringToSec(dur) {
  if (!dur) return 0;
  // Range: "40'-45'" or "40'–45'"
  let m = dur.match(/(\d+(?:\.\d+)?)\s*['\u2019]\s*[-\u2013]\s*(\d+(?:\.\d+)?)\s*['\u2019]/);
  if (m) return (parseFloat(m[1]) + parseFloat(m[2])) / 2 * 60;
  // Range: "40-45 min"
  m = dur.match(/(\d+(?:\.\d+)?)\s*[-\u2013]\s*(\d+(?:\.\d+)?)\s*min(?:utes?)?/i);
  if (m) return (parseFloat(m[1]) + parseFloat(m[2])) / 2 * 60;
  // H:MM
  m = dur.match(/^(\d{1,2}):([0-5]\d)$/);
  if (m) return parseInt(m[1]) * 3600 + parseInt(m[2]) * 60;
  // Single: "45'"
  m = dur.match(/(\d+(?:\.\d+)?)\s*['\u2019](?!\d)/);
  if (m) return parseFloat(m[1]) * 60;
  // Single: "45 min"
  m = dur.match(/(\d+(?:\.\d+)?)\s*min(?:utes?)?/i);
  if (m) return parseFloat(m[1]) * 60;
  return 0;
}

/**
 * Compute equivalent XT miles from logged cross-training segments.
 * Uses athlete's easy pace to convert duration to equivalent running miles.
 * @param {Segment[]} segments
 * @param {number} easyPaceSecPerMile - athlete's easy pace in seconds per mile
 * @returns {number} equivalent miles
 */
export function calcXtMilesFromLog(segments, easyPaceSecPerMile) {
  if (!easyPaceSecPerMile || easyPaceSecPerMile <= 0) return 0;
  let total = 0;
  for (const seg of segments) {
    if (seg.type !== 'xt') continue;
    const sec = parseDurationStringToSec(seg.duration ?? '');
    if (sec > 0) total += sec / easyPaceSecPerMile;
  }
  return total;
}

/** Parse a split time string to seconds (handles "34", "34.5", "1:12", "4:32") */
function timeStrToSec(str) {
  if (!str) return null;
  str = str.toString().trim();
  if (/^\d+(\.\d+)?$/.test(str)) return parseFloat(str);
  const parts = str.split(':').map(Number);
  if (parts.some(isNaN)) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

/** Format seconds as a human-readable time string */
export function formatSplitTime(sec) {
  if (!sec || sec <= 0) return '';
  if (sec < 60) {
    const rounded = Math.round(sec * 10) / 10;
    return `${rounded}s`;
  }
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m < 60) return `${m}:${String(s).padStart(2, '0')}`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}:${String(mm).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Produce a canonical distance key for grouping best times */
function distanceKey(distance, distUnit) {
  const d = parseFloat(distance);
  if (!d || d <= 0) return null;
  if (distUnit === 'mi') {
    if (d === 1) return '1 Mile';
    if (d === 2) return '2 Mile';
    return `${d} mi`;
  }
  if (distUnit === 'km') {
    if (d === 1) return '1K';
    if (d === 5) return '5K';
    return `${d}K`;
  }
  // meters
  if (d >= 1000) return `${d / 1000}K`;
  return `${d}m`;
}

/**
 * Scan training log records and find the fastest logged split per distance.
 * @param {Array} trainingLogs - array of { log_date, segments }
 * @returns {{ [distKey]: { sec, timeStr, logDate } }}
 */
export function findBestSplits(trainingLogs) {
  const bests = {};
  for (const log of trainingLogs ?? []) {
    const segments = log.segments ?? [];
    const date = log.log_date;
    for (const seg of segments) {
      if (seg.type !== 'interval') continue;
      const key = distanceKey(seg.distance, seg.distUnit);
      if (!key) continue;
      for (const splitStr of seg.splits ?? []) {
        const sec = timeStrToSec(splitStr);
        if (!sec || sec <= 0) continue;
        if (!bests[key] || sec < bests[key].sec) {
          bests[key] = { sec, timeStr: formatSplitTime(sec), logDate: date };
        }
      }
    }
  }
  return bests;
}

/**
 * Group training logs into weekly mileage buckets.
 * Returns an array of 8 objects, oldest → newest.
 * @param {Array} trainingLogs - array of { log_date, total_miles }
 * @returns {{ monday: Date, label: string, miles: number }[]}
 */
export function buildWeekBars(trainingLogs) {
  // Compute Monday of the current week
  const today = new Date();
  const day = today.getDay();
  const diff = day === 0 ? 6 : day - 1;
  const thisMonday = new Date(today);
  thisMonday.setDate(today.getDate() - diff);
  thisMonday.setHours(0, 0, 0, 0);

  // Build 16 week buckets: 15 weeks ago → this week
  const weeks = Array.from({ length: 16 }, (_, i) => {
    const monday = new Date(thisMonday);
    monday.setDate(thisMonday.getDate() - (15 - i) * 7);
    const label = monday.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
    return { monday: new Date(monday), label, miles: 0 };
  });

  for (const log of trainingLogs ?? []) {
    const logDate = new Date(log.log_date + 'T00:00:00');
    for (const week of weeks) {
      const sunday = new Date(week.monday);
      sunday.setDate(week.monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);
      if (logDate >= week.monday && logDate <= sunday) {
        week.miles += log.total_miles || 0;
        break;
      }
    }
  }

  return weeks;
}
